import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, type PublicSiteSettings } from "@/lib/api";
import { DEFAULT_BRANDING } from "@/lib/branding";

type BrandingContextValue = PublicSiteSettings & {
  loading: boolean;
  refresh: () => Promise<void>;
  courseImage: (url?: string) => string;
};

const BrandingContext = createContext<BrandingContextValue | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<PublicSiteSettings>(DEFAULT_BRANDING as PublicSiteSettings);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.getPublicSettings();
      setSettings(data);
    } catch {
      setSettings(DEFAULT_BRANDING as PublicSiteSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const courseImage = useCallback(
    (url?: string) => {
      if (url) {
        if (url.startsWith("http")) return url;
        if (url.startsWith("/api/")) {
          const base = settings.site_url || window.location.origin;
          return `${base.replace(/\/$/, "")}${url}`;
        }
        return url;
      }
      return settings.course_default;
    },
    [settings.course_default, settings.site_url]
  );

  const value = useMemo(
    () => ({ ...settings, loading, refresh, courseImage }),
    [settings, loading, refresh, courseImage]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    return {
      ...(DEFAULT_BRANDING as PublicSiteSettings),
      loading: false,
      refresh: async () => {},
      courseImage: (url?: string) => url || DEFAULT_BRANDING.course_default,
    };
  }
  return ctx;
}
